import io from 'socket.io-client';
import { datadogLogs } from '@datadog/browser-logs';
import { isRemoteTeleprompter } from 'main/components/show/cuesheet/cuesheetViewHelper';

/**
 * 'Singleton' socket object
 */
var socket;

/**
 * Check if we should use sockets..for printable URLs, the answer is no :)
 *
 * @private
 */
const _shouldUseSockets = () =>
  window.location.href.indexOf('printable') === -1 &&
  (window.location.href.indexOf('guest') === -1 || isRemoteTeleprompter());

/**
 * A stub socket object
 *
 * @type {{on: Function}}
 * @private
 */
const _socketStub = {
  on: (eventName) => {
    console.warn(
      `Applied event handler for event ${eventName} - but web sockets are disabled.`,
    );
  },
  emit: (eventName) => {
    console.warn(`Fired event ${eventName} - but web sockets are disabled.`);
  },

  io: {
    on: (eventName) => {
      console.warn(
        `Applied io event handler for event ${eventName} - but web sockets are disabled.`,
      );
    },
    emit: (eventName) => {
      console.warn(
        `Fired io event ${eventName} - but web sockets are disabled.`,
      );
    },
  },
};

/**
 * Create a 'real' socket.io socket object
 * @returns {*}
 * @private
 */
const _createSocket = () => {
  let options = {
    query: {},
    transports: ['polling', 'websocket'],
    tryAllTransports: true,
  };

  const { pathname } = window.location;
  if (pathname.indexOf('desktop/teleprompter/guest') !== -1) {
    const pathArray = pathname.substring(1).split('/');
    const [, teamId, , eventId, , showId] = pathArray;
    if (isValidId(teamId) && isValidId(eventId) && isValidId(showId)) {
      options.query = {
        teamId,
        eventId,
        showId,
      };
    }
  }

  options.withCredentials = true;

  const socket = io(_getSocketServerUrl(), options);

  const buildErrorContext = (error) => ({
    message: error?.message,
    description: error?.description,
    type: error?.type,
    statusCode: error?.context?.status ?? error?.context?.statusCode,
    transport: socket.io.engine?.transport?.name,
  });

  socket.on('connect_error', (error) => {
    const context = buildErrorContext(error);
    console.error('Socket connection error:', context);
    datadogLogs.logger.error('socket connect_error', context);
  });

  socket.io.on('reconnect_error', (error) => {
    const context = buildErrorContext(error);
    console.error('Socket reconnect error:', context);
    datadogLogs.logger.error('socket reconnect_error', context);
  });

  socket.io.on('reconnect_failed', () => {
    const context = { transport: socket.io.engine?.transport?.name };
    console.error('Socket reconnect failed', context);
    datadogLogs.logger.error('socket reconnect_failed', context);
  });

  // Fires when polling -> websocket upgrade fails (classic proxy/firewall symptom).
  // The Manager creates a new engine on every (re)connect, so re-attach on 'open'.
  socket.io.on('open', () => {
    socket.io.engine?.on?.('upgradeError', (error) => {
      const context = buildErrorContext(error);
      console.error('Socket upgrade error:', context);
      datadogLogs.logger.error('socket upgradeError', context);
    });
  });

  return socket;
};

const _getSocketServerUrl = () => {
  if (window.realtimeEndpoint) return window.realtimeEndpoint;

  let loc = window.location;
  let socketUrl = loc.host;

  if (socketUrl.indexOf(':') == -1 && loc.port) {
    socketUrl += ':' + loc.port;
  }

  return socketUrl;
};

export default {
  get() {
    if (socket) return socket;
    if (_shouldUseSockets()) {
      socket = _createSocket();
    } else {
      socket = _socketStub;
    }
    return socket;
  },
};

function isValidId(id) {
  const regex = /^[a-fA-F0-9]{24}$/;
  return id && id.match(regex);
}
